import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import BreakingNews from '../components/BreakingNews';
import Footer from '../components/Footer';
import { Eye, Calendar, Share2, Facebook, Twitter } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { getImageUrl } from '../utils/imageHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=1200&h=800&fit=crop';

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchArticle();
    fetchRelatedArticles();
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/articles/${id}`);
      setArticle(response.data);
      setError(false);
    } catch (error) {
      console.error('Failed to fetch article:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedArticles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/articles?limit=4`);
      setRelatedArticles(response.data.filter(a => a.id !== id).slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch related articles:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <BreakingNews />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <BreakingNews />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600">The article you're looking for doesn't exist.</p>
          <Button className="mt-6 bg-red-600 hover:bg-red-700 text-white" onClick={() => window.location.href = '/'}>
            Go Back Home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <BreakingNews />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            {/* Featured Image */}
            <div className="relative h-96 bg-gray-100">
              <img
                src={getImageUrl(article.image_url || article.image)}
                alt={article.title_english || article.titleEnglish}
                className="w-full h-full object-contain"
                onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
              />
              {(article.is_breaking || article.isBreaking) && (
                <div className="absolute top-4 left-4">
                  <Badge className="bg-red-600 text-white px-3 py-1 text-sm font-bold uppercase">
                    Breaking
                  </Badge>
                </div>
              )}
            </div>

            {/* Article Content */}
            <div className="p-8">
              {/* Meta Info */}
              <div className="flex items-center space-x-3 mb-4">
                <Badge className="bg-red-600 text-white px-3 py-1 text-sm uppercase">
                  {article.category_bengali || article.categoryBengali || article.category}
                </Badge>
                {article.location && <span className="text-gray-600">{article.location}</span>}
              </div>

              {/* Title */}
              <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {article.title_bengali || article.titleBengali}
              </h1>
              <h2 className="text-xl text-gray-600 mb-6">
                {article.title_english || article.titleEnglish}
              </h2>

              {/* Article Stats */}
              <div className="flex items-center justify-between border-y border-gray-200 py-4 mb-6">
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>{article.date || new Date(article.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4" />
                    <span>{article.views || 0} views</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button size="sm" variant="outline" className="hover:bg-blue-50">
                    <Facebook className="w-4 h-4 mr-1" />
                    Share
                  </Button>
                  <Button size="sm" variant="outline" className="hover:bg-blue-50">
                    <Twitter className="w-4 h-4 mr-1" />
                    Tweet
                  </Button>
                  <Button size="sm" variant="outline">
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Featured Image */}
              {article.image_url && (
                <div className="mb-8 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={getImageUrl(article.image_url)}
                    alt={article.title_bengali || article.titleBengali}
                    className="w-full h-auto max-h-[600px] object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Article Body */}
              <div className="prose prose-lg max-w-none">
                {/* Display Bengali content (primary) */}
                {article.content_bengali && (
                  <div className="mb-6">
                    <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                      {article.content_bengali}
                    </div>
                  </div>
                )}
                
                {/* Display English content (if available) */}
                {article.content_english && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {article.content_english}
                    </div>
                  </div>
                )}
                
                {/* Fallback if no content available */}
                {!article.content_bengali && !article.content_english && (
                  <p className="text-gray-500 italic">No content available for this article.</p>
                )}
              </div>
            </div>
          </div>

          {/* Related Articles */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Related Articles</h3>
            {relatedArticles.length > 0 ? (
              <div className="space-y-4">
                {relatedArticles.map((relatedArticle) => (
                  <a
                    key={relatedArticle.id}
                    href={`/article/${relatedArticle.id}`}
                    className="flex space-x-4 hover:bg-gray-50 p-3 rounded-lg transition-colors duration-200"
                  >
                    <img
                      src={getImageUrl(relatedArticle.image_url || relatedArticle.image)}
                      alt={relatedArticle.title_english || relatedArticle.titleEnglish}
                      className="w-24 h-24 object-contain bg-gray-100 rounded"
                      onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                    />
                    <div className="flex-1">
                      <Badge variant="outline" className="text-xs mb-2 border-red-600 text-red-600">
                        {relatedArticle.category_bengali || relatedArticle.categoryBengali || relatedArticle.category}
                      </Badge>
                      <h4 className="font-semibold text-gray-900 line-clamp-2 mb-1">
                        {relatedArticle.title_bengali || relatedArticle.titleBengali}
                      </h4>
                      <p className="text-xs text-gray-500">{relatedArticle.date || new Date(relatedArticle.created_at).toLocaleDateString()}</p>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No related articles available</p>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ArticlePage;